import { FeatureFlag } from 'app/api/services/site-configs/types';
import type { GetState, ThunkDispatchType } from 'app/redux/types';

import { accountFullName } from 'app/lib/account/selectors';
import type { BettingEntitiesSelectors } from 'app/lib/betting/betting-entities';
import { getFeatureFlagFromWindow } from 'app/lib/feature-flags';
import { OpenSignDialog, openSignDialog } from 'app/modules/Signing/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { createImmutableObjects, createMatchEntity } from '../utils/entries';
import { InsertInQueueForAdd, insertInQueueForAdd } from './entries';

import { OpenUserNameModal, openUserNameModal } from '.';

// ---

export type AddBetAction = (
  matchId: number,
  betId: number,
) => (
  dispatch: ThunkDispatchType<InsertInQueueForAdd | OpenSignDialog | OpenUserNameModal>,
  getState: GetState
) => void;

export type AddBetFactoryAction = (
  matchesSelector: BettingEntitiesSelectors['matches'],
  sportsSelector: BettingEntitiesSelectors['sports'],
  objectsSelector: BettingEntitiesSelectors['objects'],
) => AddBetAction;

export const addBetFactory: AddBetFactoryAction = (
  matchesSelector,
  sportsSelector,
  objectsSelector,
) => (
  (matchId, betId) => (
    dispatch,
    getState,
  ): void => {
    const state = getState();
    const shouldOpenAuthModal = getFeatureFlagFromWindow(
      FeatureFlag.OPEN_AUTH_MODAL_ON_ADD_BET_WHEN_NOT_AUTHORIZED,
    );
    const requireUsernameOnAddBet = getFeatureFlagFromWindow(
      FeatureFlag.REQUIRE_USERNAME_ON_ADD_BET,
    );
    const isLoggedIn = selectIsLoggedIn(state);
    const userFullName = accountFullName(state);

    if (shouldOpenAuthModal && !isLoggedIn) {
      dispatch(openSignDialog());

      return;
    }

    if (requireUsernameOnAddBet && !userFullName && isLoggedIn) {
      dispatch(openUserNameModal());

      return;
    }

    const objects = objectsSelector(state);
    const match = matchesSelector(state).get(matchId);

    const bet = match && match.bets.get(betId);
    const stake = match && bet && match.stakes.get(bet.stakeId);

    const sport = match && sportsSelector(state).get(match?.sportId);

    if (match && bet && stake && sport) {
      const matchObjects = createImmutableObjects(match, objects);

      const entity = createMatchEntity(match, matchObjects);

      dispatch(insertInQueueForAdd({
        bet,
        stake,
        sport,
        match: entity,
      }));
    }
  }
);
